/**
 * SupabaseFactoryStore — web mirror of `Sources/Store/SupabaseFactoryStore.swift`
 * (the LIVE path).
 *
 * The real read-model client, wired to `@supabase/supabase-js`. It reads the
 * factory_* tables via PostgREST (RLS-scoped to the caller's tenant) and submits
 * a FactoryCommand by INSERTing into factory_commands — the ONLY write apps are
 * allowed.
 *
 * AUTH: this store does NOT decide auth. It is constructed with the SHARED
 * `SupabaseClient` that `SupabaseAuth` signed in (via
 * `auth.signInWithPassword`). The SDK manages that session (persist + refresh)
 * and rides it on every PostgREST request AND the Realtime socket automatically,
 * so both always carry a fresh Supabase-issued `tenant_id` claim and
 * `current_tenant_id()` scopes rows. No manual token hook, no Worker mint.
 *
 * REALTIME (PRIMARY) + POLLING (FALLBACK): the factory_* tables are now in the
 * `supabase_realtime` publication with REPLICA IDENTITY FULL (prod project
 * qmttigbqvpgzpfmdmktm), and RLS applies to Realtime — each subscriber only
 * receives their own tenant's rows, scoped by the same Supabase JWT. So
 * `liveUpdates()` runs a single supervisor that seeds a full snapshot, subscribes
 * to postgres_changes on the six tables and merges each change into the in-memory
 * snapshot, and on channel error/disconnect falls back to polling while retrying
 * the channel with backoff. Exactly ONE mode is active at a time (realtime XOR
 * polling) — never two loops writing at once.
 */

import {
  type RealtimeChannel,
  type RealtimePostgresChangesPayload,
  type SupabaseClient,
} from '@supabase/supabase-js';
import {
  FactoryTable,
  type FactoryApprovalRow,
  type FactoryCommandInsert,
  type FactoryCommandRow,
  type FactoryEventRow,
  type FactoryHealthRow,
  type FactoryPrTaskRow,
  type FactoryTaskRow,
} from '../models/factory';
import {
  emptySnapshot,
  fetchSnapshotDefault,
  FactoryStoreError,
  pollSnapshots,
  type FactorySnapshot,
  type FactoryStore,
  type LiveUpdateOptions,
} from './FactoryStore';

/** Max events kept in memory (factory_events is append-only + high-volume). */
const MAX_EVENTS = 200;

export class SupabaseFactoryStore implements FactoryStore {
  private readonly client: SupabaseClient;

  /**
   * @param client  the SHARED `SupabaseClient` that `SupabaseAuth` signed in.
   *                Reads + Realtime ride on the SDK-managed session, so no
   *                access-token hook is needed here (the SDK carries the tenant
   *                JWT on every request and on the Realtime socket).
   */
  constructor(client: SupabaseClient) {
    this.client = client;
  }

  async fetchTasks(): Promise<FactoryTaskRow[]> {
    const { data, error } = await this.client
      .from(FactoryTable.tasks)
      .select('*')
      .order('priority', { ascending: true, nullsFirst: false });
    if (error) throw new FactoryStoreError(error.message);
    return (data ?? []) as FactoryTaskRow[];
  }

  async fetchPRTasks(): Promise<FactoryPrTaskRow[]> {
    const { data, error } = await this.client
      .from(FactoryTable.prTasks)
      .select('*')
      .order('updated_at', { ascending: false });
    if (error) throw new FactoryStoreError(error.message);
    return (data ?? []) as FactoryPrTaskRow[];
  }

  async fetchApprovals(): Promise<FactoryApprovalRow[]> {
    const { data, error } = await this.client
      .from(FactoryTable.approvals)
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    if (error) throw new FactoryStoreError(error.message);
    return (data ?? []) as FactoryApprovalRow[];
  }

  async fetchEvents(limit: number): Promise<FactoryEventRow[]> {
    const { data, error } = await this.client
      .from(FactoryTable.events)
      .select('*')
      .order('event_ts', { ascending: false })
      .limit(limit);
    if (error) throw new FactoryStoreError(error.message);
    return (data ?? []) as FactoryEventRow[];
  }

  async fetchHealth(): Promise<FactoryHealthRow | null> {
    const { data, error } = await this.client.from(FactoryTable.health).select('*').limit(1);
    if (error) throw new FactoryStoreError(error.message);
    return ((data ?? [])[0] as FactoryHealthRow | undefined) ?? null;
  }

  async fetchSnapshot(): Promise<FactorySnapshot> {
    return fetchSnapshotDefault(this);
  }

  async submit(command: FactoryCommandInsert): Promise<FactoryCommandRow> {
    // Apps may ONLY INSERT factory_commands. RLS `with check` enforces
    // tenant_id = current_tenant_id(); status/timestamps default server-side.
    const { data, error } = await this.client
      .from(FactoryTable.commands)
      .insert(command)
      .select('*')
      .single();
    if (error) throw new FactoryStoreError(error.message);
    return data as FactoryCommandRow;
  }

  // MARK: - Realtime (PRIMARY) + polling fallback
  //
  // Owns the live-update loop. At any instant it is in EXACTLY one mode:
  // seeding → realtime → (on drop) polling-while-retrying → back to seeding.
  // `AppEnvironment.startLiveUpdates()` calls this and forwards each snapshot to
  // React unchanged — the interface is identical to the polling path.

  liveUpdates(
    onSnapshot: (snap: FactorySnapshot) => void,
    options: LiveUpdateOptions = {},
  ): () => void {
    const intervalMs = options.intervalMs ?? 5000;
    let cancelled = false;
    let snap: FactorySnapshot = emptySnapshot();
    let channel: RealtimeChannel | null = null;
    let stopPolling: (() => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let backoff = 1000;
    const maxBackoff = 30000;

    const emit = () => {
      if (!cancelled) onSnapshot(snap);
    };

    // Polling is the SAFETY NET: it only runs while we are NOT subscribed.
    const startPolling = () => {
      if (stopPolling || cancelled) return;
      stopPolling = pollSnapshots(
        this,
        (s) => {
          snap = s;
          emit();
        },
        intervalMs,
      );
    };
    const stopPollingNow = () => {
      stopPolling?.();
      stopPolling = null;
    };

    const teardownChannel = () => {
      if (channel) {
        void this.client.removeChannel(channel);
        channel = null;
      }
    };

    // Realtime dropped → poll (safety net) and retry the channel with backoff.
    const fallback = () => {
      if (cancelled) return;
      teardownChannel();
      startPolling(); // keeps data fresh WHILE realtime is down
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        if (!cancelled) void connect();
      }, backoff);
      backoff = Math.min(backoff * 2, maxBackoff);
    };

    const connect = async () => {
      if (cancelled) return;

      // (1) SEED — authoritative full fetch (correct ordering/filtering).
      try {
        snap = await this.fetchSnapshot();
        emit();
      } catch {
        fallback();
        return;
      }
      if (cancelled) return;

      // Ensure the Realtime socket carries the tenant JWT so RLS scopes it. The
      // SDK already wires this from the signed-in session on auth changes; we set
      // it explicitly from the current session token as belt-and-suspenders.
      try {
        const { data } = await this.client.auth.getSession();
        await this.client.realtime.setAuth(data.session?.access_token ?? null);
      } catch {
        /* best-effort; a null/expired token simply yields no rows (fail closed) */
      }
      if (cancelled) return;

      // (2) SUBSCRIBE — six tables on one channel; merge each change.
      channel = this.client
        .channel('factory-read-model')
        .on<FactoryTaskRow>(
          'postgres_changes',
          { event: '*', schema: 'public', table: FactoryTable.tasks },
          (p) => applyTaskChange(p),
        )
        .on<FactoryPrTaskRow>(
          'postgres_changes',
          { event: '*', schema: 'public', table: FactoryTable.prTasks },
          (p) => applyPRChange(p),
        )
        .on<FactoryApprovalRow>(
          'postgres_changes',
          { event: '*', schema: 'public', table: FactoryTable.approvals },
          (p) => applyApprovalChange(p),
        )
        .on<FactoryEventRow>(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: FactoryTable.events },
          (p) => applyEventInsert(p),
        )
        .on<FactoryHealthRow>(
          'postgres_changes',
          { event: '*', schema: 'public', table: FactoryTable.health },
          (p) => applyHealthChange(p),
        )
        // factory_commands is the app's WRITE channel; the read-model snapshot
        // has no command projection, so we don't subscribe it for mutations.
        .subscribe((status) => {
          if (cancelled) return;
          if (status === 'SUBSCRIBED') {
            // Realtime is healthy → stop the fallback poller (single writer) and
            // reset backoff.
            stopPollingNow();
            backoff = 1000;
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            fallback();
          }
        });
    };

    // MARK: change → snapshot mapping (mirrors the Swift store)

    const rowStatus = (r: { status?: string | null }) => r.status ?? 'pending';

    /** tasks: upsert/replace by id (delete → remove), priority-asc ordering. */
    const applyTaskChange = (p: RealtimePostgresChangesPayload<FactoryTaskRow>) => {
      if (p.eventType === 'DELETE') {
        const id = (p.old as Partial<FactoryTaskRow>).id;
        snap = { ...snap, tasks: snap.tasks.filter((t) => t.id !== id) };
      } else {
        const row = p.new as FactoryTaskRow;
        const tasks = snap.tasks.filter((t) => t.id !== row.id);
        tasks.push(row);
        tasks.sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));
        snap = { ...snap, tasks };
      }
      emit();
    };

    /** pr_tasks: upsert/replace by id (delete → remove), updated_at-desc. */
    const applyPRChange = (p: RealtimePostgresChangesPayload<FactoryPrTaskRow>) => {
      if (p.eventType === 'DELETE') {
        const id = (p.old as Partial<FactoryPrTaskRow>).id;
        snap = { ...snap, prTasks: snap.prTasks.filter((t) => t.id !== id) };
      } else {
        const row = p.new as FactoryPrTaskRow;
        const prTasks = snap.prTasks.filter((t) => t.id !== row.id);
        prTasks.push(row);
        prTasks.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
        snap = { ...snap, prTasks };
      }
      emit();
    };

    /** approvals: pending-only queue. Upsert if pending, else drop; created_at-desc. */
    const applyApprovalChange = (p: RealtimePostgresChangesPayload<FactoryApprovalRow>) => {
      if (p.eventType === 'DELETE') {
        const id = (p.old as Partial<FactoryApprovalRow>).id;
        snap = { ...snap, approvals: snap.approvals.filter((a) => a.id !== id) };
      } else {
        const row = p.new as FactoryApprovalRow;
        const approvals = snap.approvals.filter((a) => a.id !== row.id);
        if (rowStatus(row) === 'pending') approvals.push(row);
        approvals.sort((a, b) => b.created_at.localeCompare(a.created_at));
        snap = { ...snap, approvals };
      }
      emit();
    };

    /** events: append-only. Prepend the INSERT (newest-first) and cap. */
    const applyEventInsert = (p: RealtimePostgresChangesPayload<FactoryEventRow>) => {
      const row = p.new as FactoryEventRow;
      const events = [row, ...snap.events].slice(0, MAX_EVENTS);
      snap = { ...snap, events };
      emit();
    };

    /** health: one row per tenant — replace (delete → null). */
    const applyHealthChange = (p: RealtimePostgresChangesPayload<FactoryHealthRow>) => {
      snap = { ...snap, health: p.eventType === 'DELETE' ? null : (p.new as FactoryHealthRow) };
      emit();
    };

    void connect();

    // Unsubscribe: stop every mode and cancel any pending retry.
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      stopPollingNow();
      teardownChannel();
    };
  }
}
